"use client";

import Image from "next/image";
import { usePathname } from "next/navigation";
import { storyblokEditable, type SbBlokData } from "@storyblok/react/rsc";

import { useTriggerAction, type BlokAction } from "@/lib/action-link";
import { resolveBg } from "@/lib/bg-color";
import { regionFromPathname, type Region } from "@/lib/region";
import {
  resolveLink,
  resolveRichTextHref,
  type StoryblokLink,
} from "@/lib/storyblok-link";

type Asset = { filename?: string; alt?: string };

type RichTextMark = { type: string; attrs?: { href?: string; target?: string } };
type RichTextNode = {
  type: string;
  text?: string;
  content?: RichTextNode[];
  marks?: RichTextMark[];
};
type RichTextDoc = { type: "doc"; content?: RichTextNode[] };

type ClinicsHeroBlok = SbBlokData & {
  eyebrow?: string;
  title?: string;
  description?: string | RichTextDoc;
  image?: Asset;
  primary_label?: string;
  primary_link?: StoryblokLink;
  primary_action?: BlokAction | "";
  secondary_label?: string;
  secondary_link?: StoryblokLink;
  bg_color?: unknown;
};

function richTextHasContent(node: RichTextNode | undefined): boolean {
  if (!node) return false;
  if (node.type === "text") return !!node.text?.trim();
  return (node.content ?? []).some(richTextHasContent);
}

// The editor toolbar is limited to bold, italic and links, so only those marks need handling.
function renderNode(
  node: RichTextNode,
  key: number,
  onDark: boolean,
  region?: Region,
): React.ReactNode {
  switch (node.type) {
    case "paragraph":
      return <p key={key}>{node.content?.map((n, i) => renderNode(n, i, onDark, region))}</p>;
    case "hard_break":
      return <br key={key} />;
    case "text": {
      let el: React.ReactNode = node.text ?? "";
      for (const mark of node.marks ?? []) {
        if (mark.type === "bold") el = <strong>{el}</strong>;
        else if (mark.type === "italic") el = <em>{el}</em>;
        else if (mark.type === "link" && mark.attrs?.href && (node.text ?? "").trim()) {
          const href = resolveRichTextHref(mark.attrs.href, region);
          const isExternal = /^https?:\/\//i.test(href);
          const target = isExternal ? "_blank" : mark.attrs.target;
          el = (
            <a
              href={href}
              target={target}
              rel={target === "_blank" ? "noopener noreferrer" : undefined}
              className={`underline underline-offset-2 hover:opacity-80 ${
                onDark ? "text-white" : "text-purple-03"
              }`}
            >
              {el}
            </a>
          );
        }
      }
      return <span key={key}>{el}</span>;
    }
    default:
      return node.content?.map((n, i) => renderNode(n, i, onDark, region)) ?? null;
  }
}

function Description({
  value,
  onDark,
  region,
}: {
  value: string | RichTextDoc;
  onDark: boolean;
  region?: Region;
}) {
  const baseClass = `text-body md:text-h6 leading-[1.6] ${onDark ? "text-white/85" : "text-black/70"}`;
  if (typeof value === "string") {
    return value.trim() ? <p className={baseClass}>{value}</p> : null;
  }
  if (!richTextHasContent(value)) return null;
  return (
    <div className={`flex flex-col gap-3 ${baseClass}`}>
      {value.content?.map((n, i) => renderNode(n, i, onDark, region))}
    </div>
  );
}

function ArrowIcon() {
  return (
    <svg width="17" height="11" viewBox="0 0 17 11" fill="none" aria-hidden>
      <path
        d="M1 5.5h14.5M11 1l4.5 4.5L11 10"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

const PRIMARY_CLASS =
  "inline-flex h-12 w-full cursor-pointer items-center justify-center rounded-[8px] bg-purple-03 px-5 text-body font-bold text-white transition-colors hover:bg-purple-04 md:h-14 md:w-fit md:px-7";

export default function ClinicsHero({ blok }: { blok: ClinicsHeroBlok }) {
  const pathname = usePathname();
  const region = regionFromPathname(pathname);
  const { bg, onDark } = resolveBg(blok.bg_color, "dusk-01");
  const triggerAction = useTriggerAction();

  const primary = resolveLink(blok.primary_link, region);
  const primaryAction = blok.primary_action || null;
  const showPrimary = Boolean(blok.primary_label && (primaryAction || primary.href !== "#"));

  const secondary = resolveLink(blok.secondary_link, region);
  const showSecondary = Boolean(blok.secondary_label && secondary.href !== "#");

  const imageSrc = blok.image?.filename;

  return (
    <section {...storyblokEditable(blok)} className={bg}>
      <div className="mx-auto w-full max-w-[1280px] px-[clamp(1.25rem,4vw,2.5rem)] py-[clamp(2.5rem,5vw,4.5rem)]">
        <div className="flex flex-col gap-8 lg:flex-row lg:items-center lg:gap-[88px]">
          <div className="flex flex-col gap-5 lg:min-w-0 lg:flex-1">
            {blok.eyebrow && (
              <span
                className={`text-caption font-bold tracking-wider uppercase ${
                  onDark ? "text-white/70" : "text-purple-03"
                }`}
              >
                {blok.eyebrow}
              </span>
            )}

            {blok.title && (
              <h1
                className={`text-h3 md:text-[64px] leading-[1.054] font-bold tracking-[0.02em] ${
                  onDark ? "text-white" : "text-purple-05"
                }`}
              >
                {blok.title}
              </h1>
            )}

            {blok.description && (
              <Description value={blok.description} onDark={onDark} region={region} />
            )}

            {(showPrimary || showSecondary) && (
              <div className="mt-1 flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-6">
                {showPrimary &&
                  (primaryAction ? (
                    <button
                      type="button"
                      onClick={(e) => triggerAction(primaryAction, e)}
                      className={PRIMARY_CLASS}
                    >
                      {blok.primary_label}
                    </button>
                  ) : (
                    <a
                      href={primary.href}
                      {...(primary.target ? { target: primary.target } : {})}
                      className={PRIMARY_CLASS}
                    >
                      {blok.primary_label}
                    </a>
                  ))}

                {showSecondary && (
                  <a
                    href={secondary.href}
                    {...(secondary.target ? { target: secondary.target } : {})}
                    className={`text-body inline-flex w-fit items-center gap-[7px] py-2.5 leading-[1.054] font-extrabold hover:underline lg:-translate-y-0.5 ${
                      onDark ? "text-white" : "text-purple-03"
                    }`}
                  >
                    {blok.secondary_label}
                    <ArrowIcon />
                  </a>
                )}
              </div>
            )}
          </div>

          {imageSrc && (
            <div className="w-full lg:w-[560px] lg:shrink-0">
              <div className="relative aspect-560/400 overflow-hidden rounded-[24px]">
                <Image
                  src={imageSrc}
                  alt={blok.image?.alt || ""}
                  fill
                  sizes="(min-width: 1115px) 560px, 100vw"
                  className="object-cover"
                  priority
                />
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
